import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { ArrowsUpdownIcon, CaretDownIcon, CaretUpIcon, CheckSmallIcon } from '@/icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui';
import { cn } from '@/utils/tailwind.util';
export type ClassroomSortMode = 'latest' | 'name';
export function ClassroomSortDropdown({
  countLabel,
  sortMode,
  onChangeSortMode,
  compact = false,
  disabled = false,
  showDivider = true
}: {
  countLabel: string;
  sortMode: ClassroomSortMode;
  onChangeSortMode: (mode: ClassroomSortMode) => void;
  compact?: boolean;
  disabled?: boolean;
  /** 개수 라벨과 정렬 버튼 사이 구분선. 개수 라벨이 없는 화면(과제 탭)에선 끈다. */
  showDivider?: boolean;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  const SORT_LABELS: Record<ClassroomSortMode, string> = {
    latest: t('classroom:classroomSortDropdown.latest'),
    name: t('classroom:classroomSortDropdown.name')
  };
  return <div className={cn('flex shrink-0 items-center justify-end gap-2xs', compact ? 'h-[36px]' : 'h-[40px]')} data-sentry-component="ClassroomSortDropdown" data-sentry-source-file="ClassroomSortDropdown.tsx">
      {/* 개수 라벨이 없을 때 빈 span 의 여백이 남으면 검색과 정렬 사이가 벌어진다. */}
      {countLabel ? <span className={cn('flex shrink-0 items-center px-sm text-body-md-semibold text-neutral', compact ? 'h-[36px]' : 'h-[40px]')}>
          {countLabel}
        </span> : null}
      {showDivider ? <span className="h-[16px] w-0 shrink-0 border-l border-alternative" /> : null}
      <DropdownMenu modal={false} data-sentry-element="DropdownMenu" data-sentry-source-file="ClassroomSortDropdown.tsx">
        <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="ClassroomSortDropdown.tsx">
          <button type="button" disabled={disabled} className={cn('group flex shrink-0 items-center justify-center gap-3xs rounded-sm text-body-md-semibold text-neutral hover:bg-opacity-black-1 disabled:cursor-default disabled:hover:bg-transparent',
        // compact 는 정확히 36x36 이어야 한다 — px 를 주면 44 가 돼 탭마다 검색 아이콘 위치가 어긋난다.
        compact ? 'size-[36px]' : 'h-[40px] min-w-[40px] px-sm')}>
            <ArrowsUpdownIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="ArrowsUpdownIcon" data-sentry-source-file="ClassroomSortDropdown.tsx" />
            {/* compact(모바일)은 시안상 정렬 아이콘만 — 라벨·열림 토글 caret 미노출. */}
            {!compact && <>
                <span className="max-mobile-lg:hidden">
                  {SORT_LABELS[sortMode]}
                </span>
                <CaretDownIcon size="sm" fill={ICON_COLOR.neutral} className="group-data-[state=open]:hidden" />
                <CaretUpIcon size="sm" fill={ICON_COLOR.neutral} className="hidden group-data-[state=open]:block" />
              </>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[160px] rounded-sm border border-assistive bg-default px-xs py-3xs shadow-lg" data-sentry-element="DropdownMenuContent" data-sentry-source-file="ClassroomSortDropdown.tsx">
          {(['latest', 'name'] as const).map(mode => <DropdownMenuItem key={mode} className={cn('flex h-[40px] min-h-[40px] items-center justify-between rounded-sm px-2xs py-4xs text-body-sm-medium text-default hover:bg-neutral', sortMode === mode && 'bg-opacity-violet-1 text-body-sm-semibold text-strong hover:bg-opacity-violet-1')} onSelect={() => onChangeSortMode(mode)}>
              <span className="flex min-w-0 flex-1 items-center truncate">
                {SORT_LABELS[mode]}
              </span>
              {sortMode === mode ? <CheckSmallIcon size="sm" fill={ICON_COLOR.strong} className="shrink-0" /> : null}
            </DropdownMenuItem>)}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
}